// FIREBASE
import { ref, uploadBytesResumable, getDownloadURL, updateMetadata } from 'firebase/storage';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { storage, videoStorage, db } from '../../firebase';

// FUNCTIONS
import { captionsBumpFunctionFor } from './captionsFunctions';
import { v4 as uuidv4 } from 'uuid';
import base64 from 'base-64';

const DEFAULT_THUMBNAIL_URL = process.env.REACT_APP_DEFAULT_THUMBNAIL_URL;

const validateFile = (file) => {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';

        video.onloadedmetadata = () => {
            window.URL.revokeObjectURL(video.src);
            if (video.duration > 0 && video.videoWidth > 0 && video.videoHeight > 0) {
                let videoQuality;
                if (video.videoWidth >= 3840) {
                    videoQuality = '4K';
                } else if (video.videoWidth >= 1920) {
                    videoQuality = 'HD';
                } else if (video.videoWidth >= 1280) {
                    videoQuality = '720';
                } else {
                    videoQuality = 'SD';
                }
                resolve({ videoQuality, videoDuration: video.duration });
            } else {
                reject('Invalid video file');
            }
        };

        video.onerror = () => {
            window.URL.revokeObjectURL(video.src);
            reject('Invalid video file');
        };

        video.src = URL.createObjectURL(file);
    });
};

const generate16DigitVideoId = () => {
    const uniqueId = uuidv4();
    const base64Id = base64.encode(uniqueId)
        .replace(/\+/g, '-') // Replace + with -
        .replace(/\//g, '_') // Replace / with _
        .replace(/=+$/, ''); // Remove ending =
    return base64Id.slice(0, 16);
};

const generateVideoId = async () => {
    let videoId = generate16DigitVideoId();
    let videoDocRef = doc(db, "globalBumps", videoId);
    let videoDoc = await getDoc(videoDocRef);

    while (videoDoc.exists()) {
        videoId = generate16DigitVideoId();
        videoDocRef = doc(db, "globalBumps", videoId);
        videoDoc = await getDoc(videoDocRef);
    }

    await setDoc(videoDocRef, { exists: true });
    return videoId;
};

const uploadBump = async (file, user, setUploadProgress, setVideoId, setShowPopup, setThumbnailUrl, storagePathRef, uploadTaskRef, thumbnailUrl) => {
    if (!file) return;

    if (file.size > 31 * 1024 * 1024 * 1024) {
        console.error("File size exceeds the 31GB limit:", file.size);
        return;
    }

    let videoMetadata;
    try {
        videoMetadata = await validateFile(file);
    } catch (validationError) {
        console.error("File validation error:", validationError);
        return;
    }

    const generatedVideoId = await generateVideoId();
    setVideoId(generatedVideoId);
    setShowPopup(true);

    const metadata = {
        contentType: file.type,
        customMetadata: {
            uploadedBy: user.uid,
            description: 'Video upload',
            videoFileName: file.name,
            videoThumbnail: thumbnailUrl || DEFAULT_THUMBNAIL_URL,
            videoQuality: videoMetadata.videoQuality,
            videoDuration: videoMetadata.videoDuration.toString()
        }
    };


    const storageRef = ref(videoStorage, `${user.uid}/${generatedVideoId}`);
    storagePathRef.current = storageRef;
    const uploadTask = uploadBytesResumable(storageRef, file, metadata);
    uploadTaskRef.current = uploadTask;

    uploadTask.on('state_changed',
        (snapshot) => {
            const videoProgress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setUploadProgress(videoProgress);
        },
        (error) => {
        },
        async () => {
            try {
                await getDownloadURL(uploadTask.snapshot.ref);

                const thumbnailstorageRef = ref(storage, `bumpThumbnails/${user.uid}/${generatedVideoId}`);
                let thumbnailDownloadURL = thumbnailUrl;

                try {
                    thumbnailDownloadURL = await getDownloadURL(thumbnailstorageRef);

                    const newMetadata = {
                        customMetadata: {
                            videoThumbnail: thumbnailDownloadURL
                        }
                    };
                    await updateMetadata(storageRef, newMetadata);
                    setThumbnailUrl(thumbnailDownloadURL);
                } catch (error) {
                }

                setUploadProgress(100);

                const payload = {
                    videoMetadata: {
                        uploadedBy: user.uid,
                        videoId: generatedVideoId,
                        description: 'Video upload',
                        videoFileName: file.name,
                        videoThumbnail: thumbnailDownloadURL || DEFAULT_THUMBNAIL_URL,
                        videoQuality: videoMetadata.videoQuality,
                        videoDuration: videoMetadata.videoDuration.toString()
                    }
                };

                // Call the captions function sized for this file
                await captionsBumpFunctionFor(file.size, videoMetadata.videoDuration)(payload);

                // Clear local storage
                const localStorageKey = `videoCache_${user.uid}_BumpedVideos`;
                localStorage.removeItem(localStorageKey);
                localStorage.removeItem(`recentBump_${user.uid}`);

            } catch (error) {
                console.error("Error during upload completion or thumbnail URL handling:", error);
            }
        }
    );
};

export { uploadBump, validateFile, generate16DigitVideoId, generateVideoId };

